import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * A handoff code is a signed JWT, so we can read its `portal` claim WITHOUT
 * redeeming it. Redemption is one-time and server-side, so a code fed to the wrong
 * portal would be burned AND would store a session this app can't use — landing the
 * user in the wrong product with no explanation. Checking first turns that into a
 * precise error, and is how a swapped/misconfigured portal URL gets noticed.
 */
function intendedPortal(code) {
  try {
    return JSON.parse(atob(code.split('.')[1])).portal || null;
  } catch {
    return null;
  }
}

/**
 * Lands here from another portal's "Switch Portal" via a one-time code in the URL
 * fragment (#code=…). Redeems it for a patient session and goes home.
 */
export default function PortalSwitch() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = window.location.hash || '';
    const code = new URLSearchParams(hash.replace(/^#/, '')).get('code');
    window.history.replaceState(null, '', window.location.pathname);

    if (!code) {
      setError('This switch link is missing its code.');
      return;
    }
    const target = intendedPortal(code);
    if (target && target !== 'patient') {
      setError(
        `This link is for the ${target} portal, but it opened the patient portal. ` +
        'The portals are probably running on swapped addresses — check which app is ' +
        'served on each port.'
      );
      return;
    }
    axios
      .post(`${API_URL}/api/v1/auth/switch-portal/redeem`, { code })
      .then((res) => {
        loginWithToken(res.data.user);
        navigate('/', { replace: true });
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'This switch link is invalid or has expired.');
      });
  }, [loginWithToken, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
      {error ? (
        <>
          <Typography color="error" align="center">{error}</Typography>
          <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
            Go to sign in
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">Switching portals…</Typography>
        </>
      )}
    </Box>
  );
}
